import { Grid2, IconButton, Switch, Tooltip, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { FETCH_CHAT_SETTINGS } from "@chat/graphql/queries/fetchChatSettings";
import { Query, Mutation } from "@/app/graphql/codegen";
import { EDIT_CHAT_SETTINGS } from "@company/graphql/mutations/updateChatSettings";
import { FETCH_CONTEXT_SELECTION_FOR_CHAT } from "@chat/graphql/queries/fetchContextSelectionForChat";
import { icon } from "@/app/style/icons";

const GraphToggle = () => {
    const { id: chatId } = useParams();

    const { data, loading } = useQuery<Pick<Query, "chat">>(FETCH_CHAT_SETTINGS, {
        variables: { id: chatId || "" },
        skip: !chatId,
    });

    const { data: contextData, loading: contextLoading } = useQuery<Pick<Query, "contextForChat">>(FETCH_CONTEXT_SELECTION_FOR_CHAT, {
        variables: { chatId: chatId || "" },
        skip: !chatId,
    });

    const [editSettings] = useMutation<Pick<Mutation, "editChatSettings">, { id: string; graphEnabled: boolean; gptOnlyEnabled?: boolean }>(EDIT_CHAT_SETTINGS);

    // Get graph mode from context (primary source)
    const isGraphEnabled = contextData?.contextForChat?.graphEnabled || false;

    const toggleGraphMode = async () => {
        if (!chatId) return;

        await editSettings({
            variables: {
                id: chatId,
                graphEnabled: !isGraphEnabled,
                // Enforce exclusivity: when enabling graph, disable GPT-only
                gptOnlyEnabled: !isGraphEnabled ? false : undefined,
            },
            refetchQueries: [
                {
                    query: FETCH_CHAT_SETTINGS,
                    variables: { id: chatId },
                },
                {
                    query: FETCH_CONTEXT_SELECTION_FOR_CHAT,
                    variables: { chatId },
                },
            ],
        });
    };

    return (
        <Grid2 container size={12} alignItems="center" justifyContent="space-between">
            <Grid2 container alignItems={"center"} size="auto" spacing={0.5}>
                <Grid2>
                    <Typography>SmartSearch</Typography>
                </Grid2>

                <Grid2>
                    <Tooltip
                        title={
                            "SmartSearch ist eine intelligente Navigationsfunktion, die automatisch erkennt, welche Gesetze und Dokumente für eine Frage relevant sind. \n\nIst SmartSearch aktiviert, werden immer alle verfügbaren Dokumente berücksichtigt – eine zusätzliche Auswahl einzelner Dokumente ist dann nicht möglich."
                        }
                    >
                        <IconButton
                            size={"small"}
                            color={"default"}
                            sx={{
                                opacity: 1,
                            }}
                        >
                            <icon.info sx={{ color: "semantic.icon.informative" }} />
                        </IconButton>
                    </Tooltip>
                </Grid2>
            </Grid2>

            <Grid2>
                <Switch
                    checked={isGraphEnabled}
                    onChange={toggleGraphMode}
                    disabled={loading}
                    sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "semantic.highlight.primary",
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "semantic.highlight.primary",
                        },
                    }}
                />
            </Grid2>
        </Grid2>
    );
};

export default GraphToggle;
